// Collapsible datatalk chat panel for the Vorhersage (prediction) tab: left side is the
// LLM chat (LayerChatPanel, toggled between "prediction"-mode datatalk and "leitlinien"
// RAG mode), right side is VorhersageKontextPanel giving the professional an at-a-glance
// summary of upcoming predictions + top SHAP drivers while they chat. Optionally blends
// the ML prediction context into the RAG prompt via `predictionContextEnabled`.
import { useState } from "react"
import {
    AnalysisServiceGetPHQ8LocalShapResponsePayload,
    AnalysisServiceGetPHQ8PredictionResponsePayload,
    PHQ8ModelMetadata,
} from "pokal_api"
import LayerChatPanel from "../chat/shared/LayerChatPanel"
import VorhersageKontextPanel from "./VorhersageKontextPanel"
import AnalyseTriggerCard from "../shared/AnalyseTriggerCard"
import AnalyseModeToggle from "../shared/AnalyseModeToggle"

type Props = {
    patientId: string
    isOpen: boolean
    onToggle: () => void
    metadata: PHQ8ModelMetadata | null
    predictions: AnalysisServiceGetPHQ8PredictionResponsePayload | null
    localShap: AnalysisServiceGetPHQ8LocalShapResponsePayload | null
}

type PredictionMode = "prediction" | "leitlinien"

export default function PredictionAnalysisPanel({
    patientId,
    isOpen,
    onToggle,
    metadata,
    predictions,
    localShap,
}: Props) {
    const [mode, setMode] = useState<PredictionMode>("prediction")
    const [predictionContextEnabled, setPredictionContextEnabled] = useState(false)
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([])
    const [similarityThreshold, setSimilarityThreshold] = useState(0.3)
    const [topK, setTopK] = useState(5)
    if (!isOpen) {
        return (
            <AnalyseTriggerCard
                title="Vorhersage-Chat"
                description="Stellen Sie Fragen zu den Vorhersagen und Einflussfaktoren."
                onOpen={onToggle}
            />
        )
    }

    return (
        <div>
            {/* Collapse bar */}
            <div
                className="bg-teal-50 border border-teal-300 rounded-t-xl px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors"
                onClick={onToggle}
            >
                <span className="text-sm font-semibold text-teal-800">Vorhersage-Chat</span>
                <span className="text-xs text-teal-600 font-medium">▲ Schließen</span>
            </div>

            {/* 2-column content */}
            <div className="flex space-x-4 border-x border-b border-teal-300 rounded-b-xl p-4 bg-white h-[480px]">
                {/* LEFT: mode toggle + chat */}
                <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                        <AnalyseModeToggle
                            mode={mode}
                            onModeChange={m => setMode(m as PredictionMode)}
                            dataMode="prediction"
                            dataModeLabel="nur Daten"
                        />
                        {mode === "leitlinien" && (
                            <button
                                type="button"
                                onClick={() => setPredictionContextEnabled(v => !v)}
                                className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border transition-colors ${
                                    predictionContextEnabled
                                        ? "bg-teal-100 border-teal-400 text-teal-700 font-semibold"
                                        : "bg-white border-gray-300 text-gray-500 hover:border-teal-400"
                                }`}
                            >
                                <span
                                    className={`inline-block w-3 h-3 rounded-full ${predictionContextEnabled ? "bg-teal-500" : "bg-gray-300"}`}
                                />
                                Vorhersagedaten einbeziehen
                            </button>
                        )}
                    </div>
                    <LayerChatPanel
                        patientId={patientId}
                        mode={mode}
                        pageContext="prediction"
                        predictionContextEnabled={predictionContextEnabled}
                        selectedDocumentIds={selectedDocumentIds}
                        onSelectionChange={setSelectedDocumentIds}
                        similarityThreshold={similarityThreshold}
                        onThresholdChange={setSimilarityThreshold}
                        topK={topK}
                        onTopKChange={setTopK}
                    />
                </div>

                {/* RIGHT: orient panel — per-day predictions + top drivers */}
                <div className="flex-1 min-w-0 overflow-y-auto">
                    <VorhersageKontextPanel
                        metadata={metadata}
                        predictions={predictions}
                        localShap={localShap}
                    />
                </div>
            </div>
        </div>
    )
}
